import React from 'react';
import { ShieldCheck, Clock, RefreshCw, Database, Check, Sparkles, Zap, Target, Cpu, BarChart3, Lock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface MicroTrustBadgesProps {
  size?: 'sm' | 'md';
  align?: 'left' | 'center' | 'right' | 'center-mobile';
  className?: string;
}

interface FeatureBadgesProps {
  features: Array<{ icon?: React.ReactNode; label: string; tooltip?: string }>;
  size?: 'sm' | 'md';
  align?: 'left' | 'center' | 'right' | 'center-mobile';
  variant?: 'trust' | 'features' | 'minimal';
  className?: string;
}

/**
 * Business logic: Concise, truthful micro-badges near primary CTAs to reduce risk perception
 * and increase trust at decision points. Copy is intentionally short; details live in tooltips.
 */
const MicroTrustBadges: React.FC<MicroTrustBadgesProps> = ({ size = 'sm', align = 'center', className }) => {
  const textSize = size === 'sm' ? 'text-xs md:text-sm' : 'text-sm md:text-base';
  const iconSize = size === 'sm' ? 'h-4 w-4 md:h-5 md:w-5' : 'h-5 w-5 md:h-6 md:w-6';
  const justify = align === 'left' ? 'justify-start' : 
                  align === 'right' ? 'justify-end' : 
                  align === 'center-mobile' ? 'justify-center md:justify-start' : 
                  'justify-center';

  const items: Array<{ icon: React.ReactNode; label: string; tooltip: string }> = [
    { icon: <Clock className={iconSize} />, label: '24–48h setup', tooltip: 'Typical provisioning time for standard bundles.' },
    { icon: <RefreshCw className={iconSize} />, label: 'Cancel anytime', tooltip: 'No long-term commitments. Cancel whenever you like.' },
    { icon: <ShieldCheck className={iconSize} />, label: 'Secure', tooltip: 'Hardened configurations and best-practice defaults.' },
    { icon: <Database className={iconSize} />, label: 'Backed up', tooltip: 'Periodic backups and restore procedures available.' },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <div className={`flex flex-wrap ${justify} gap-2 ${className ?? ''}`}>
        {items.map((item) => (
          <Tooltip key={item.label}>
            <TooltipTrigger asChild>
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border bg-card/80 shadow-sm ${textSize}`}>
                <span className="text-primary">{item.icon}</span>
                <span className="text-foreground/90">{item.label}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <span className="text-xs">{item.tooltip}</span>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
};

/**
 * Modern feature badges for replacing ugly bullet points in cards
 */
export const FeatureBadges: React.FC<FeatureBadgesProps> = ({
  features,
  size = 'sm',
  align = 'left',
  variant = 'features',
  className
}) => {
  const textSize = size === 'sm' ? 'text-xs md:text-sm' : 'text-sm md:text-base';
  const iconSize = size === 'sm' ? 'h-3 w-3 md:h-4 md:w-4' : 'h-4 w-4 md:h-5 md:w-5';
  const justify = align === 'left' ? 'justify-start' : 
                  align === 'right' ? 'justify-end' : 
                  align === 'center-mobile' ? 'justify-center md:justify-start' : 
                  'justify-center';

  // Enhanced icons for different feature types
  const getFeatureIcon = (label: string) => {
    const lowerLabel = label.toLowerCase();
    if (lowerLabel.includes('ai') || lowerLabel.includes('intelligent') || lowerLabel.includes('smart')) {
      return <Sparkles className={iconSize} />;
    }
    if (lowerLabel.includes('performance') || lowerLabel.includes('speed') || lowerLabel.includes('fast')) {
      return <Zap className={iconSize} />;
    }
    if (lowerLabel.includes('secure') || lowerLabel.includes('security') || lowerLabel.includes('compliance')) {
      return <Lock className={iconSize} />;
    }
    if (lowerLabel.includes('analytics') || lowerLabel.includes('data') || lowerLabel.includes('dashboard')) {
      return <BarChart3 className={iconSize} />;
    }
    if (lowerLabel.includes('compute') || lowerLabel.includes('processing') || lowerLabel.includes('cpu')) {
      return <Cpu className={iconSize} />;
    }
    if (lowerLabel.includes('target') || lowerLabel.includes('precision') || lowerLabel.includes('accurate')) {
      return <Target className={iconSize} />;
    }
    return <Check className={iconSize} />;
  };

  const getBadgeStyles = () => {
    switch (variant) {
      case 'trust':
        return 'flex items-center justify-start gap-2 px-3 py-2 rounded-full border bg-card/80 shadow-sm w-full';
      case 'minimal':
        return 'flex items-center justify-start gap-2 px-2 py-2 rounded-md bg-slate-100 text-slate-700 w-full';
      case 'features':
      default:
        return 'flex items-center justify-start gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 w-full';
    }
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div className={`flex flex-col gap-2 ${className ?? ''}`}>
        {features.map((feature, index) => {
          const icon = feature.icon || getFeatureIcon(feature.label);
          const hasTooltip = feature.tooltip && feature.tooltip.length > 0;

          const badge = (
            <div key={index} className={`${getBadgeStyles()} ${textSize}`}>
              <span className="text-blue-600 flex-shrink-0">{icon}</span>
              <span className="font-medium">{feature.label}</span>
            </div>
          );

          if (hasTooltip) {
            return (
              <Tooltip key={index}>
                <TooltipTrigger asChild>
                  {badge}
                </TooltipTrigger>
                <TooltipContent>
                  <span className="text-xs">{feature.tooltip}</span>
                </TooltipContent>
              </Tooltip>
            );
          }

          return badge;
        })}
      </div>
    </TooltipProvider>
  );
};

export default MicroTrustBadges;

